import type {
    LoginResponse,
    ChatsResponse,
    ChatResponse,
    MediaResponse,
    MediaFile,
    ModelsResponse,
    SettingsResponse,
    PromptsResponse,
    ApiResponse,
    User,
    ActiveModel,
    SystemPrompt,
    ChatExport,
} from './types';

// API Base URL: 개발 시 /api (Vite proxy), 프로덕션 시 Worker URL
const API_BASE = import.meta.env.VITE_API_URL || '/api';

// =====================================================
// API Client with Error Handling
// =====================================================

async function fetchApi<T>(
    endpoint: string,
    options: RequestInit = {}
): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            ...options.headers,
        },
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || `HTTP error ${response.status}`);
    }

    return data;
}

// =====================================================
// Auth API
// =====================================================

export const authApi = {
    login: async (username: string, password: string): Promise<LoginResponse> => {
        return fetchApi('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ username, password }),
        });
    },

    logout: async (): Promise<ApiResponse> => {
        return fetchApi('/auth/logout', { method: 'POST' });
    },

    me: async (): Promise<{ success: boolean; user: User }> => {
        return fetchApi('/auth/me');
    },

    register: async (username: string, password: string): Promise<LoginResponse> => {
        return fetchApi('/auth/register', {
            method: 'POST',
            body: JSON.stringify({ username, password }),
        });
    },

    changePassword: async (currentPassword: string, newPassword: string): Promise<ApiResponse> => {
        return fetchApi('/auth/password', {
            method: 'PUT',
            body: JSON.stringify({ currentPassword, newPassword }),
        });
    },

    invalidateSessions: async (): Promise<ApiResponse> => {
        return fetchApi('/auth/invalidate-sessions', { method: 'POST' });
    },
};

// =====================================================
// Chat API
// =====================================================

export const chatApi = {
    list: async (search?: string): Promise<ChatsResponse> => {
        const params = search ? `?search=${encodeURIComponent(search)}` : '';
        return fetchApi(`/chats${params}`);
    },

    get: async (id: number): Promise<ChatResponse> => {
        return fetchApi(`/chats/${id}`);
    },

    create: async (title?: string): Promise<ChatResponse> => {
        return fetchApi('/chats', {
            method: 'POST',
            body: JSON.stringify({ title }),
        });
    },

    update: async (id: number, data: { title?: string; model?: string }): Promise<ChatResponse> => {
        return fetchApi(`/chats/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    delete: async (id: number): Promise<ApiResponse> => {
        return fetchApi(`/chats/${id}`, { method: 'DELETE' });
    },

    deleteMessages: async (chatId: number, messageIds: number[]): Promise<ApiResponse & { deleted?: number }> => {
        return fetchApi(`/chats/${chatId}/messages`, {
            method: 'DELETE',
            body: JSON.stringify({ messageIds }),
        });
    },

    export: async (id: number, format: 'json' | 'markdown' = 'json'): Promise<Blob> => {
        const response = await fetch(`${API_BASE}/chats/${id}/export?format=${format}`, {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Export failed');
        return response.blob();
    },

    import: async (data: ChatExport): Promise<ChatResponse> => {
        return fetchApi('/chats/import', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    exportAll: async (): Promise<Blob> => {
        const response = await fetch(`${API_BASE}/chats/export-all`, {
            credentials: 'include',
        });
        if (!response.ok) throw new Error('Export failed');
        return response.blob();
    },

    importAll: async (data: { version: string; chats: unknown[] }): Promise<ApiResponse & { message?: string }> => {
        return fetchApi('/chats/import-all', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },
};

// =====================================================
// Gemini API
// =====================================================

export const geminiApi = {
    getModels: async (): Promise<ModelsResponse> => {
        return fetchApi('/gemini/models');
    },

    sendMessage: async (
        message: string,
        chatId?: number,
        model?: string,
        systemPromptId?: number,
        attachments?: { type: string; data: string }[],
        onChunk?: (event: { type: string; content?: string; chatId?: number; r2_path?: string; data?: string; mimeType?: string }) => void
    ): Promise<void> => {
        const response = await fetch(`${API_BASE}/gemini/chat`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                message,
                chatId,
                model,
                systemPromptId,
                attachments,
                timezoneOffset: new Date().getTimezoneOffset(),
            }),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Chat request failed');
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
            throw new Error('No response body');
        }

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n');

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            onChunk?.(data);
                        } catch {
                            // Skip invalid JSON
                        }
                    }
                }
            }
        } finally {
            reader.releaseLock();
        }
    },
};

// =====================================================
// Media API
// =====================================================

export const mediaApi = {
    list: async (folder?: string): Promise<MediaResponse> => {
        const params = folder ? `?folder=${encodeURIComponent(folder)}` : '';
        return fetchApi(`/media${params}`);
    },

    upload: async (file: File, folder?: string, metadata?: object): Promise<ApiResponse & { file?: MediaFile }> => {
        const formData = new FormData();
        formData.append('file', file);
        if (folder) formData.append('folder', folder);
        if (metadata) formData.append('metadata', JSON.stringify(metadata));

        const response = await fetch(`${API_BASE}/media/upload`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
        });

        return response.json();
    },

    move: async (id: number, targetFolder: string): Promise<ApiResponse & { newPath?: string }> => {
        return fetchApi(`/media/${id}/move`, {
            method: 'PUT',
            body: JSON.stringify({ targetFolder }),
        });
    },

    delete: async (id: number): Promise<ApiResponse> => {
        return fetchApi(`/media/${id}`, { method: 'DELETE' });
    },

    sync: async (): Promise<ApiResponse & { added: number; removed: number }> => {
        return fetchApi('/media/sync', { method: 'POST' });
    },

    getFileUrl: (path: string): string => {
        return `${API_BASE}/media/file/${path}`;
    },

    createFolder: async (name: string, parent?: string): Promise<ApiResponse & { folder?: string }> => {
        return fetchApi('/media/folder', {
            method: 'POST',
            body: JSON.stringify({ name, parent }),
        });
    },
};

// =====================================================
// Settings API
// =====================================================

export const settingsApi = {
    get: async (): Promise<SettingsResponse> => {
        return fetchApi('/settings');
    },

    update: async (settings: {
        api_key?: string;
        auto_save_r2?: boolean;
        selected_model?: string;
        // R2 Configuration
        r2_account_id?: string;
        r2_access_key_id?: string;
        r2_secret_access_key?: string;
        r2_bucket_name?: string;
    }): Promise<ApiResponse> => {
        return fetchApi('/settings', {
            method: 'PUT',
            body: JSON.stringify(settings),
        });
    },

    getPrompts: async (): Promise<PromptsResponse> => {
        return fetchApi('/settings/prompts');
    },

    createPrompt: async (prompt: {
        title: string;
        content: string;
        is_active?: boolean;
    }): Promise<{ success: boolean; prompt: SystemPrompt }> => {
        return fetchApi('/settings/prompts', {
            method: 'POST',
            body: JSON.stringify(prompt),
        });
    },

    updatePrompt: async (
        id: number,
        prompt: Partial<{ title: string; content: string; is_active: boolean }>
    ): Promise<{ success: boolean; prompt: SystemPrompt }> => {
        return fetchApi(`/settings/prompts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(prompt),
        });
    },

    deletePrompt: async (id: number): Promise<ApiResponse> => {
        return fetchApi(`/settings/prompts/${id}`, { method: 'DELETE' });
    },
};

// =====================================================
// Admin API
// =====================================================

export const adminApi = {
    getUsers: async (): Promise<{ success: boolean; users: User[] }> => {
        return fetchApi('/admin/users');
    },

    createUser: async (user: {
        username: string;
        password: string;
        role?: 'admin' | 'user';
    }): Promise<{ success: boolean; user: User }> => {
        return fetchApi('/admin/users', {
            method: 'POST',
            body: JSON.stringify(user),
        });
    },

    deleteUser: async (id: number): Promise<ApiResponse> => {
        return fetchApi(`/admin/users/${id}`, { method: 'DELETE' });
    },

    resetPassword: async (id: number, password: string): Promise<ApiResponse> => {
        return fetchApi(`/admin/users/${id}/password`, {
            method: 'PUT',
            body: JSON.stringify({ password }),
        });
    },

    updateRole: async (id: number, role: 'admin' | 'user'): Promise<{ success: boolean; user: User }> => {
        return fetchApi(`/admin/users/${id}/role`, {
            method: 'PUT',
            body: JSON.stringify({ role }),
        });
    },

    getModels: async (): Promise<{ success: boolean; models: ActiveModel[] }> => {
        return fetchApi('/admin/models');
    },

    fetchModels: async (): Promise<ApiResponse & { added: number; updated: number }> => {
        return fetchApi('/admin/models/fetch', { method: 'POST' });
    },

    updateModel: async (
        id: number,
        updates: { is_active?: boolean; display_name?: string; is_image_model?: boolean }
    ): Promise<{ success: boolean; model: ActiveModel }> => {
        return fetchApi(`/admin/models/${id}`, {
            method: 'PUT',
            body: JSON.stringify(updates),
        });
    },

    deleteModel: async (id: number): Promise<ApiResponse> => {
        return fetchApi(`/admin/models/${id}`, { method: 'DELETE' });
    },

    getStats: async (): Promise<{
        success: boolean;
        stats: {
            users: number;
            chats: number;
            messages: number;
            media_files: number;
            media_size_bytes: number;
        };
    }> => {
        return fetchApi('/admin/stats');
    },
};
